import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, darkColors, lighterVariations, StyledFlex, Tag } from '@tryhackme/thm-ui-components';
import styled, { css } from 'styled-components';

import {
  CommonHeaderContentStyles,
  DarkModeMainAdjustmentStyles,
  DarkModeTableAdjustmentStyles,
  CommonBadgeStyles,
  DarkBackgroundSummaryBadgeStyles,
  MainContentBadgeStyles,
} from 'src/common/legacy-styles';
import { TextAndBackgroundColorStyles } from 'src/common/legacy-styles/text-and-bg-color';

import { ACCORDION_ANIMATION_DURATION } from './accordion.constants';

export const StyledAccordionWrapper = styled.div<{ $isDark: boolean; $isTaskComplete?: boolean }>`
  box-shadow: inset 16px 0 0 -7px ${({ theme, $isTaskComplete }) => ($isTaskComplete ? theme.colors.primary.main : theme.colors.error.main)};
  border-radius: 0.5rem;
  margin-bottom: 1.6rem;
  scroll-margin-top: 2rem;
  svg {
    margin-left: 0 !important;
  }
  ${TextAndBackgroundColorStyles}
  ${CommonHeaderContentStyles}
  ${DarkModeTableAdjustmentStyles}
  & > #accordion-details {
    max-height: 0;
    overflow: hidden;
  }

  .accordion-details-enter {
    max-height: 0;
    overflow: hidden;
  }
  .accordion-details-enter-active {
    max-height: 400rem !important;
    transition: max-height ${ACCORDION_ANIMATION_DURATION}ms ease;
  }
  .accordion-details-enter-done {
    max-height: fit-content !important;
  }
  .accordion-details-exit {
    max-height: 400rem !important;
    overflow: hidden !important;
    transition: max-height ${ACCORDION_ANIMATION_DURATION / 1.5}ms ease;
  }
  .accordion-details-exit-active {
    max-height: 0 !important;
    overflow: hidden !important;
  }
  .accordion-details-exit-done {
    max-height: 0 !important;
    overflow: hidden !important;
  }
`;

export const StyledAccordionSummary = styled.div<{ $splitScreenOpen: boolean; $isLocked: boolean }>`
  background-color: ${({ theme, $isLocked }) => ($isLocked ? theme.colors.grey[900] : theme.colors.secondary.light)};
  display: flex;
  justify-content: space-around;
  font-family: ${({ theme }) => theme.fonts.familySansPro} !important;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: ${({ $isLocked }) => ($isLocked ? 'auto' : 'pointer')};
  line-height: 2rem;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 1.4rem 1.8rem;
  }

  ${CommonBadgeStyles}
  ${DarkBackgroundSummaryBadgeStyles}

    ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      padding: 1.4rem 1.8rem;
    `}
`;

export const StyledAccordionSummaryLeft = styled.div<{ $splitScreenOpen: boolean }>`
  background-color: inherit;
  width: 100%;
  display: flex;
  align-items: center;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    display: block;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      display: block;
    `}
`;

export const StyledTaskNumber = styled.span<{ $isTaskComplete?: boolean; $splitScreenOpen: boolean }>`
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  line-height: 2rem;
  color: ${({ theme, $isTaskComplete }) => ($isTaskComplete ? theme.colors.primary.main : darkColors.error.main)};
  padding: 1.4rem 1.2rem 1.4rem 1.8rem;
  display: inline-block;
  min-width: fit-content;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 0 1.2rem 0 0;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      padding: 0 1.2rem 0 0;
    `}
`;

export const StyledUncheckedCircleIcon = styled(FontAwesomeIcon)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.secondary.lighter};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
`;

export const StyledCheckedCircleIcon = styled(FontAwesomeIcon)`
  display: inline-block;
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  color: ${({ theme }) => theme.colors.primary.main};
`;

export const StyledTaskTitle = styled.span`
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  line-height: ${({ theme }) => theme.fonts.lineHeight.default};
  color: ${({ theme }) => theme.colors.grey['100']};
  padding: 0 1.2rem;
`;

export const StyledAccordionSummaryRight = styled.div`
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const StyledAngledDownWrapper = styled.span<{ $splitScreenOpen: boolean }>`
  display: inline-block;
  margin: 1.5rem 1.8rem 1.5rem 2.4rem;

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    margin: 0 0 0 2.4rem;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      margin: 0 0 0 2.4rem;
    `}
`;

export const StyledAngledDown = styled(FontAwesomeIcon)<{ $isOpen: boolean }>`
  color: ${({ theme }) => theme.colors.grey['100']};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  transform: rotate(${({ $isOpen }) => ($isOpen ? '-180deg' : '0deg')});
  transition: transform 0.3s ease;
`;

export const StyledDownloadableIcon = styled(FontAwesomeIcon)`
  color: ${darkColors.info.main};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  display: inline-block;
`;

export const StyledCloudDetailsIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  display: inline-block;
`;

export const StyledServerWindowIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  display: inline-block;
`;

export const StyledIconWrapper = styled.span<{ $splitScreenOpen: boolean }>`
  position: relative;
  &:hover {
    & > div:first-of-type {
      visibility: visible;
      opacity: 1;
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-left: 0.5rem;
  }

  ${({ $splitScreenOpen }) =>
    $splitScreenOpen &&
    css`
      margin-left: 0.5rem;
    `}
`;

export const StyledTooltip = styled.div<{ $width?: number }>`
  position: absolute;
  right: 3rem;
  top: -1rem;
  width: ${({ $width }) => ($width ? `${$width}` : `21`)}rem;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  & > div {
    background-color: ${({ theme }) => theme.colors.offBlack};
    color: ${({ theme }) => theme.colors.grey['100']};
  }
  & ::before {
    border-color: transparent transparent transparent ${({ theme }) => theme.colors.offBlack};
  }
`;

export const StyledAccordionDetails = styled.div<{ $isTaskComplete?: boolean; $isDark: boolean }>`
  display: flex;
  flex-direction: column;
  height: fit-content;
  overflow: hidden;
  position: relative;
  border-left-width: 0.4rem;
  border-left-style: solid;
  border-left-color: ${({ theme, $isTaskComplete }) =>
    $isTaskComplete ? theme.colors.primary.main : theme.colors.error.main};

  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;

  ${TextAndBackgroundColorStyles}
  ${CommonBadgeStyles}
  ${MainContentBadgeStyles}
  ${DarkModeMainAdjustmentStyles}

  background-color: ${({ theme, $isDark }) => ($isDark ? 'rgba(28, 37, 57,1)' : theme.colors.background.light)};
`;

export const StyledAccordionDetailsTop = styled.div<{ applyDefaultStyles: boolean; $isExperimentEnabled?: boolean }>`
  ${({ applyDefaultStyles, $isExperimentEnabled }) =>
    applyDefaultStyles &&
    `
    float: right;
    text-align: right;
    padding: ${$isExperimentEnabled ? 0 : '3rem 3rem 0 0'}
  `}
`;

export const StyledContentButton = styled(Button)<{ applyDefaultStyles: boolean }>`
  ${({ applyDefaultStyles }) => (applyDefaultStyles ? 'margin: 0 0 1rem 1rem;' : 'margin-bottom: 1.6rem;')}
`;

export const StyledContentButtonIcon = styled(FontAwesomeIcon)`
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  display: inline-block;
  margin-right: 0.5rem;
`;

const tooltipWrapperHeight = 500;
export const StyledGlossaryTooltipWrapper = styled.div<{
  position: { y: number; x: number };
}>`
  top: ${({ position }) => position.y - tooltipWrapperHeight}px;
  left: ${({ position }) => position.x}px;
  bottom: 0;
  right: 0;
  height: ${tooltipWrapperHeight}px;
  z-index: 9999;
  position: absolute;
  display: grid;
  align-items: end;
  -webkit-box-align: unset;
`;

export const StyledGlossaryTooltipContent = styled.div`
  width: max-content;
`;

export const StyledLockedTag = styled(Tag)<{ $splitScreenOpen: boolean }>`
  border: none;
  background: ${lighterVariations.blue.darker};
  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    margin-right: ${({ $splitScreenOpen }) => ($splitScreenOpen ? 0 : '1.6rem')};
  }
`;

export const StyledLockedTagLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey[200]};
  font-size: 1.6rem;
  line-height: 1.8rem;
`;

export const StyledLockedTagWrapper = styled(StyledFlex)`
  margin-left: 2.4rem;
`;
